import {
  useSalesServiceSalesOrdersModificationPriceCreate,
  useSalesServiceSalesOrdersModifyCreate,
  useSalesServiceSalesOrdersPartialUpdate,
  useSalesServiceSalesProductsPriceCreate
} from '@insurance/api/queries'
import {
  ApiError,
  OrderInspectionStatus,
  OrderStatus
} from '@insurance/api/requests'
import { RefundMoneyButton } from '@insurance/components'
import { useAppNavigate } from '@insurance/hooks'
import { paths } from '@insurance/routes'
import { ProductCode } from '@insurance/types'
import { replaceEmptyWithUndefined, showApiErrors } from '@shared/utils'
import { Button, Flex, Form } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { useTranslation } from 'react-i18next'

import { usePropertyInsuranceValues } from './PropertyInsuranceContext'
import {
  formProps,
  normalizeCommonData,
  normalizeModifyOrderData,
  normalizeOrderData,
  orderParentOrChildId,
  removePropertyValuesFromStorage
} from './utils'

type Props = {
  steps: number
  onReadyToSaveData?: () => void
}

export const PropertyInsuranceControls = ({
  steps,
  onReadyToSaveData
}: Props) => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()
  const { isEditable, order } = usePropertyInsuranceValues()
  const { stepKey, prevStepKey, priceKey, orderKey, isOnlineKey } = formProps(t)
  const step = Form.useWatch(stepKey.name, { preserve: true })
  const orderId = Form.useWatch(orderKey.name, { preserve: true })
  const isOnlineOrder = Form.useWatch(isOnlineKey.name, { preserve: true })
  const price = Form.useWatch(priceKey.name, { preserve: true })

  const navigate = useAppNavigate()

  const hasPrevStep = step > 0
  const isCalculateStep = step === 1
  const isReadyToApprove = step === steps - 1 && !!orderId
  const isPreparePersons = !isCalculateStep && !isReadyToApprove

  const status =
    step >= 1
      ? OrderInspectionStatus.INSPECTION
      : OrderInspectionStatus.DATA_ENTRY

  const handleFinishStepError = async (error: unknown) => {
    if (error instanceof ApiError && error.body?.contractor && step !== 0) {
      handleChangeStep(0)

      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      )
    }

    showApiErrors(error, formInstance)
  }

  const removeAllFromStorage = () => {
    removePropertyValuesFromStorage(order?.id)
    removePropertyValuesFromStorage(orderId)
  }

  const handleChangeStep = (currentStep: number) => {
    formInstance.setFieldValue(stepKey.name, currentStep)
    formInstance.setFieldValue(prevStepKey.name, step)
    onReadyToSaveData?.()
  }

  const handlePrevStep = () => {
    const prevStep = step - 1

    if (isEditable || !orderId) handleChangeStep(prevStep)
    else
      updateOrder({
        id: orderId,
        requestBody: {
          stage: prevStep,
          status: !prevStep ? OrderInspectionStatus.DATA_ENTRY : status
        }
      })
  }

  const { mutate: updateOrder, isPending: isUpdateOrderPending } =
    useSalesServiceSalesOrdersPartialUpdate({
      onSuccess: (data, variable) => {
        const newStep = variable?.requestBody?.stage || 0
        handleChangeStep(newStep)

        if (!newStep) {
          formInstance.setFieldValue(priceKey.name, undefined)

          return
        }

        formInstance.setFieldValue(priceKey.name, {
          currency: data?.price_data?.currency,
          amount: Number(Number(data?.price_data?.price)?.toFixed(2))
        })
        formInstance.setFieldValue(orderKey.name, data.id)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
      onError: (error: AnyObject) => {
        const err = { ...error, body: error?.body?.property }
        handleFinishStepError(err)
      }
    })

  const { mutate: calculate, isPending: isCalculatingLoading } =
    useSalesServiceSalesProductsPriceCreate({
      onSuccess: (data) => {
        if (data.order?.id) {
          const nextStage = step === steps - 1 ? step : step + 1
          updateOrder({
            id: data.order.id,
            requestBody: { stage: nextStage, status }
          })
        }
      },
      onError: handleFinishStepError
    })

  const { mutate: calculateModify, isPending: isCalculatingModifyLoading } =
    useSalesServiceSalesOrdersModificationPriceCreate({
      onSuccess: (data) => {
        handleChangeStep(step + 1)

        formInstance.setFieldValue(priceKey.name, {
          currency: data?.price_data?.currency,
          amount: Number(Number(data?.price_data?.price)?.toFixed(2))
        })
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
      onError: (error: AnyObject) => {
        const err = {
          ...error,
          body: { ...error?.body?.property, ...error?.body }
        }
        handleFinishStepError(err)
      }
    })

  const {
    mutate: calculateModifyRefund,
    isPending: isCalculatingModifyRefundLoading
  } = useSalesServiceSalesOrdersModificationPriceCreate({
    onSuccess: (data) => {
      formInstance.setFieldValue(priceKey.name, {
        currency: data?.price_data?.currency,
        amount: Number(Number(data?.price_data?.price)?.toFixed(2))
      })
    },
    onError: handleFinishStepError
  })

  const { mutateAsync: modifyOrder, isPending: isModifyOrderLoading } =
    useSalesServiceSalesOrdersModifyCreate({
      onSuccess: (order) => {
        formInstance.setFieldValue(orderKey.name, order.id)
      },
      onError: handleFinishStepError
    })

  const handleCalculate = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    if (order?.id) {
      const bodyValues = normalizeModifyOrderData(values, order)

      if (
        orderParentOrChildId(order) &&
        (!order?.child || order?.child?.status === OrderStatus.CANCELED)
      )
        await modifyOrder({
          id: order?.id,
          requestBody: replaceEmptyWithUndefined({ ...bodyValues, status })
        })

      return calculateModify({
        id: order.id,
        requestBody: replaceEmptyWithUndefined(bodyValues)
      })
    }

    if (step === 0) {
      handleChangeStep(1)
      return
    }

    const isMovingToCustomerStep = step === 1 && step + 1 === steps - 1
    const isOnCustomerStep = step === steps - 1
    const includeCustomer = isMovingToCustomerStep || isOnCustomerStep
    const nextStage = isOnCustomerStep ? step : step + 1

    const bodyValues = normalizeCommonData(
      values,
      !values?.order_id,
      includeCustomer
    )

    if (values?.order_id)
      updateOrder({
        id: values.order_id,
        requestBody: {
          stage: nextStage,
          status,
          property: replaceEmptyWithUndefined(bodyValues)
        }
      })
    else calculate({ code: ProductCode.PROPERTY, requestBody: bodyValues })
  }

  const handleReadyToReview = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    const orderStatus = isOnlineOrder
      ? OrderInspectionStatus.INSPECTION
      : OrderInspectionStatus.OFFER_READY

    if (order?.id) {
      const bodyValues = normalizeModifyOrderData(values, order)

      const id =
        order.status === OrderStatus.REJECTED || !orderParentOrChildId(order)
          ? order?.parent?.id || order.id
          : order.id

      return modifyOrder(
        {
          id,
          requestBody: replaceEmptyWithUndefined({
            ...bodyValues,
            status: orderStatus
          })
        },
        {
          onSuccess: (order) => {
            removeAllFromStorage()
            navigate(
              order.id ? paths.orders.details(order.id) : paths.salesPanel.root
            )
          }
        }
      )
    }

    const bodyValues = normalizeOrderData(values)

    updateOrder(
      {
        id: orderId,
        requestBody: {
          stage: step,
          status: orderStatus,
          property: bodyValues
        }
      },
      {
        onSuccess: (order) => {
          removeAllFromStorage()
          navigate(
            order?.id ? paths.orders.details(order.id) : paths.salesPanel.root
          )
        }
      }
    )
  }

  const handleCalculateRefund = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)
    const bodyValues = normalizeModifyOrderData(values, order)

    if (order?.id)
      calculateModifyRefund({ requestBody: bodyValues!, id: order?.id })
  }

  const handleOnlyModify = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    if (!order?.id) return

    const bodyValues = normalizeModifyOrderData(values, order)

    const id =
      order.status === OrderStatus.REJECTED || !orderParentOrChildId(order)
        ? order?.parent?.id || order.id
        : order.id

    modifyOrder(
      {
        id,
        requestBody: replaceEmptyWithUndefined({
          ...bodyValues,
          status: OrderInspectionStatus.OFFER_READY
        })
      },
      {
        onSuccess: (order) => {
          removeAllFromStorage()
          navigate(
            order.id ? paths.orders.details(order.id) : paths.salesPanel.root
          )
        }
      }
    )
  }

  const orderPrice = {
    amount: order?.full_price ? Number(order?.full_price) : 0
  }

  const canRefund = isEditable && orderPrice?.amount > price?.amount

  return (
    <Flex justify="end" gap={12}>
      {hasPrevStep && (
        <Button
          color="primary"
          variant="outlined"
          loading={isUpdateOrderPending}
          onClick={handlePrevStep}
        >
          {t('prev_step')}
        </Button>
      )}

      {isCalculateStep && (
        <Button
          type="primary"
          loading={
            isCalculatingLoading ||
            isUpdateOrderPending ||
            isCalculatingModifyLoading
          }
          onClick={handleCalculate}
        >
          {t('calculate')}
        </Button>
      )}

      {isPreparePersons && (
        <Button
          type="primary"
          loading={isCalculatingLoading || isUpdateOrderPending}
          onClick={handleCalculate}
        >
          {t('next_step')}
        </Button>
      )}

      {!canRefund && isReadyToApprove && (
        <Button
          type="primary"
          loading={isUpdateOrderPending || isModifyOrderLoading}
          onClick={handleReadyToReview}
        >
          {t(isOnlineOrder ? 'to_offer' : 'to_approve')}
        </Button>
      )}

      {canRefund && isReadyToApprove && (
        <RefundMoneyButton
          loading={isCalculatingModifyRefundLoading}
          disabledSelect
          onChangeRefund={handleCalculateRefund}
          onRefund={handleOnlyModify}
        />
      )}
    </Flex>
  )
}
